import type { Measurement } from "../api/types";
import type { SelectedLocation } from "../hooks/useSelectedLocation";

/**
 * Formats an ISO 8601 datetime string as a human-readable relative time,
 * e.g. "just now", "32 minutes ago", "2 hours ago".
 */
export function formatRelativeTime(isoDatetime: string): string {
  const diffMs = Date.now() - new Date(isoDatetime).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) {
    return "just now";
  }
  if (diffMin < 60) {
    return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  }
  const diffHrs = Math.round(diffMin / 60);
  return `${diffHrs} hour${diffHrs === 1 ? "" : "s"} ago`;
}

/** Temperature for display, e.g. "55°" or "13°". */
export function formatTemperature(m: Measurement): string {
  return `${Math.round(m.value)}°`;
}

/**
 * Wind line e.g. "3 mph NW".
 */
export function formatWind(speed: Measurement, direction: string): string {
  return `${Math.round(speed.value)} ${speed.unit} ${direction}`;
}

/**
 * AppBar location: prefer API place name, else formatted coordinates.
 */
export function formatLocationLabel(
  place: string | null | undefined,
  location: SelectedLocation,
): string {
  if (place) {
    return place;
  }
  return formatCoordinates(location.lat, location.lon);
}

/** AppBar secondary line, e.g. "41.4062, -73.7408". */
export function formatCoordinates(lat: number, lon: number): string {
  return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
}

/** DayCard title for non-relative periods, e.g. "Thursday". */
export function formatPeriodWeekday(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  return d.toLocaleDateString("en-US", { weekday: "long" });
}

/** DayCard date subtitle from ISO date, e.g. "May 29" (no weekday). */
export function formatPeriodDate(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/** DayCard primary label: Today/Tomorrow or weekday (not the API's long label). */
export function formatDayCardTitle(label: string, isoDate: string): string {
  if (label === "Today" || label === "Tomorrow") {
    return label;
  }
  return formatPeriodWeekday(isoDate);
}

/** "Fri 2:00 PM – Sat 2:00 AM" or "From Fri 2:00 PM" when no expiry. */
export function formatAlertWindow(effectiveDisplay: string, expiresDisplay: string | null): string {
  return expiresDisplay ? `${effectiveDisplay} – ${expiresDisplay}` : `From ${effectiveDisplay}`;
}

/** Loading fallback for day detail header from route param YYYY-MM-DD. */
export function formatParamDate(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Pass-through for API hourly time labels (e.g. "9 AM"). */
export function formatHourDisplay(timeDisplay: string): string {
  return timeDisplay;
}

/** Time portion for the forecast freshness line below the hero card. */
export function formatNwsUpdateTime(isoDatetime: string, timeZone?: string): string {
  return new Date(isoDatetime).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone,
  });
}
